import { Moment } from 'moment-timezone';
import Practitioner from '../../app/models/Practitioner';
import CalendarEvent from '../../app/models/CalendarEvent';
import { isToday, moment, __t } from '../../app/support/helpers';

export type DataQuery = {
    start: Moment,
    end: Moment
};

/**
 * Render practitioner availabilities
 *
 * @param {Practitioner} practitioner
 * @param {CalendarEvent[]} availabilities
 * @param {DataQuery} data
 * @returns {string}
 */
export function renderPractitionerAvailabilities(practitioner: Practitioner, availabilities: CalendarEvent[], data: DataQuery): string {
    // console.groupCollapsed('listing');
    // console.log(availabilities);
    const { start, end } = data;
    // @ts-ignore
    const range               = moment().range(start, end);
    const period              = Array.from(range.by('days')) as Moment[];
    const noAvailabilities    = availabilities.length === 0;
    const hasAvaibilities     = !noAvailabilities;
    const availabilitiesByDay = groupByDay(availabilities);

    const hasOnlyClosestEvent = hasAvaibilities && availabilities.every((calendarEvent) => calendarEvent.isClosest());
    // console.log(hasOnlyClosestEvent);
    // console.groupEnd();

    return `
        <div class="bar-loader top-0 hidden"></div>
        <div class="table_right">
            <a class="nav_left nav_left_mb" data-doctor-id="${ practitioner.id }" rel="nofollow" ${ isToday(start) ? 'style="display: none;"' : '' }></a>
            <a class="nav_right nav_right_mb" data-doctor-id="${ practitioner.id }" rel="nofollow"></a>
            <ul class="ul_date_value">
                ${ renderHiddenInputs(practitioner, start) }
                ${ renderCalendarDays(period) }
            </ul>
        </div>

        <div class="liste_times">
            ${ renderCalendarColumns(period, availabilitiesByDay)}
        </div>

        ${
            hasOnlyClosestEvent
                ? renderClosestEvent(availabilities[0])
                : (noAvailabilities ? renderRequestPractitionerPhoneNumber(practitioner) : '')
        }
    `;
}

/**
 * Render closest event
 *
 * @param {CalendarEvent} event
 * @returns {string}
 */
function renderClosestEvent(event: CalendarEvent): string {
    return `
        <div class="time_empty">
            <div class="bar-loader top-0 hidden"></div>
            ${ __t("Pas de disponibilité pour cette période") }
            <div class="showPhoneWrapper">
                <span class="">
                    <a href="#" class="nextAvailability show_number" data-next-availability='${JSON.stringify(event)}' rel="nofollow">
                        ${__t('Prochain RDV le')} ${event.startDate('DD/MM/YYYY')}
                    </a>
                </span>

            </div>
        </div>


    `;
}

/**
 * Render calendar desktop header
 *
 * @param {DataQuery} data
 * @returns {string}
 */
export function renderCalendarDesktopHeader(data: DataQuery): string {
    const { start, end } = data;
    // @ts-ignore
    const range          = moment().range(start, end);
    const period         = Array.from(range.by('days')) as Moment[];

    return `
        <a href="javascript:;" class="nav_left nav_left_top ${ isToday(start) ? 'hidden' : '' }" rel="nofollow"></a>
        <a href="javascript:;" class="nav_right nav_right_top" rel="nofollow"></a>
        <ul class="ul_date relative">
        <div class="bar-loader -top-1 hidden"></div>
            ${ renderCalendarDays(period) }
        </ul>
    `;
}

/**
 * Render hidden inputs
 *
 * @param {Practitioner} practitioner
 * @param {Moment} start
 * @returns {string}
 */
function renderHiddenInputs(practitioner: Practitioner, start: Moment): string {
    return `
        <input type="hidden" class="week" value="${ start.format('W') }"/>
        <input type="hidden" class="year" value="${ start.format('YYYY') }"/>
        <input type="hidden" class="doc" value="${ practitioner.id }"/>
        <input type="hidden" class="cuurent_day" value="${ start.format('YYYY-MM-DD') }"/>
        <input type="hidden" class="calendar_speciality" value="0">
    `;
}

/**
 * Render calendar days
 *
 * @param {Moment[]} period
 * @returns {string}
 */
function renderCalendarDays(period: Moment[]): string {
    return period.map((date: Moment) => {
        return renderCalendarDay(date);
    }).join('');
}

/**
 * Render calendar day
 *
 * @param {Moment} date
 * @returns {string}
 */
function renderCalendarDay(date: Moment): string {
    return `
        <li>
            <span class="date_day">${date.format('ddd')}</span>
            <span class="date_value">${date.format('DD/MM')}</span>
        </li>
    `;
}

/**
 * Render calendar columns
 *
 * @param {Moment[]} period
 * @param {{ [key: string]: CalendarEvent[] }} availabilitiesByDay
 * @returns {string}
 */
function renderCalendarColumns(period: Moment[], availabilitiesByDay: { [key: string]: CalendarEvent[] }): string {
    return period.map((date: Moment) => {
        const day            = date.format('YYYY-MM-DD');
        const availabilities = availabilitiesByDay[day] || [];
        return renderCalendarColumn(availabilities);
    }).join('');
}

/**
 * Render calendar column
 *
 * @param {CalendarEvent[]} availabilities
 * @returns {string}
 */
function renderCalendarColumn(availabilities: CalendarEvent[]): string {
    return `
        <div class="times">
            <ul class="ul_date_value2">
                <div class="date_top">
                    ${ renderCalendarEvents(availabilities.slice(0, 6)) }
                </div>

                ${ availabilities.length > 6 ? renderShowMoreButton() : '' }

                <div class="date_bottom time_more"  style="display:none;">
                    ${ renderCalendarEvents(availabilities) }
                </div>
            </ul>
        </div>
    `;
}

/**
 * Render Calendar Events
 *
 * @param {CalendarEvent[]} calendarEvents
 * @returns {string}
 */
function renderCalendarEvents(calendarEvents: CalendarEvent[]): string {
    return calendarEvents.map((calendarEvent: CalendarEvent) => {
        return renderCalendarEvent(calendarEvent);
    }).join('');
}

/**
 * Render calendar event
 *
 * @param {CalendarEvent} calendarEvent
 * @returns {string}
 */
function renderCalendarEvent(calendarEvent: CalendarEvent): string {
    return `
        <li>
            <a
                href="${ calendarEvent.isBookable() ? calendarEvent.bookingUrl() : 'javascript:void(0)' }"
                class="time ${ calendarEvent.isBookable() ? 'takethis' : 'reserved' } ${calendarEvent.isRemote() ? 'video-call-calendar' : ''}"
                rel="nofollow"
            >
                ${ calendarEvent.startTime() }
            </a>
        </li>
    `;
}

/**
 * Render show more "button"
 * @returns {string}
 */
function renderShowMoreButton(): string {
    return `
        <li>
            <a href="javascript:;" class="time more" rel="nofollow">
                ${__t('Plus..')}
            </a>
        </li>
    `;
}

/**
 * Render request practitioner phone number
 *
 * @param {Practitioner} practitioner
 * @returns {string}
 */
function renderRequestPractitionerPhoneNumber(practitioner: Practitioner): string {
    return `
        <div class="time_empty">
            <div class="bar-loader top-0 hidden"></div>
            ${ __t("Pas de disponibilité connue pour ce praticien, pour prendre rendez-vous contactez le cabinet au") }
            <div class="showPhoneWrapper">
                <span
                    class="show_number showPhone"
                    data-doctor-id="${ practitioner.id }"
                    data-action="show:doctor:phone"
                    data-client-id=""
                >
                    ${ __t("Afficher le numéro") }
                </span>

                <a href="tel:" rel="nofollow" class="show_number number phoneNumber"></a>
            </div>
        </div>
    `;
}

/**
 * Group availabilities by day
 *
 * @param {CalendarEvent[]} availabilities
 * @returns {CalendarEvent[]}
 */
function groupByDay(availabilities: CalendarEvent[]): { [key: string]: CalendarEvent[] } {
    return availabilities.reduce((acc, availability) => {
        const day = moment(availability.start).format('YYYY-MM-DD');
        if (!acc[day]) {
            acc[day] = [];
        }
        acc[day].push(availability);
        return acc;
    }, {}) as { [key: string]: CalendarEvent[] };
}
